import { toastManager } from "~/components/ui/toast";
import {
  contextSelectionDigestValue,
  parseFilesystemMemberReference,
} from "@cleo/shared/contextSelection";
import {
  isReauthAvailableRefusal,
  workingContextMemberIds,
  type AgentContextResolution,
  type ContextDeliveryMode,
} from "@cleo/contracts";
import { digestAgentContextResolution } from "@cleo/shared/agentContextResolution";
import {
  digestSessionSelection,
  parseWorldSessionSelectionReceipt,
  WORLD_SESSION_SELECTION_SCHEMA_VERSION,
  type WorldSessionSelection,
  type WorldSessionSelectionBirth,
} from "@cleo/shared/sessionSelection";
import { fetchPrimary } from "../environments/primary/fetch";
import { create } from "zustand";
import { orderSessionSelection, type SelectionOperation } from "./sessionSelectionOrder";
import {
  projectLocalBoundaryResponse,
  refreshLocalBoundaryLabels,
  type LocalBoundaryRead,
} from "./localBoundaryClient";

import {
  ROOM_UNIVERSE_PAGE_MAX,
  normalizePageFilter,
  parseRoomUniversePage,
  parseRoomUniverseSummary,
  roomUniversePageKey,
  type RoomUniversePageRequest,
  type RoomUniversePageState,
  type RoomUniverseSelectionPredicate,
  type RoomUniverseSummary,
} from "./roomUniversePaged";
import {
  normalizeWorldSessionDoorRefusalKind,
  type WorldSessionDoorRefusalKind,
} from "./worldSessionClient";
import { useNeucleosWorldStore } from "./worldStore";
import type { ContextSources } from "./contextObserver";

export interface RoomUniverseCoordinate {
  readonly worldKey: string;
  readonly roomId: string;
  readonly sessionId: string;
  readonly agentEnvironmentId: string;
  readonly agentThreadId: string;
}

export interface RoomUniverseMember {
  readonly id: string;
  readonly label: string;
}

export type RoomUniverseGroup = {
  readonly group: string;
  readonly state: string;
  readonly reason?: string;
  readonly members?: ReadonlyArray<RoomUniverseMember>;
};

/**
 * Arc SEL (BD-CAN-13): the World's stored set receipt. The legacy
 * catalog-bound receipt stays readable; catalog digests are not identity.
 */
export interface WorldContextSelection extends RoomUniverseCoordinate {
  readonly schemaVersion: "world-context-selection-v1" | "world-session-selection-v1";
  readonly selectionCheckpoint?: true;
  readonly catalogDigest?: string;
  readonly catalogCustodyDigest?: string;
  readonly selectionCut: number;
  readonly selectionDigest: string;
  readonly memberIds: ReadonlyArray<string>;
  readonly birth?: WorldSessionSelectionBirth;
  readonly expressions?: ReadonlyArray<import("@cleo/contracts").ContextSelectionExpression>;
  /**
   * SESSION LAW (principal, 15 September 2026): anything generated in a
   * session stays in the session. What this thread created, as the World
   * records it; reachable for the rest of the session without a move.
   */
  readonly createdMemberIds?: ReadonlyArray<string>;
  readonly workingContextBitmap?: string;
  readonly memberManifest?: {
    readonly manifestId: string;
    readonly manifestDigest: string;
    readonly itemCount: number;
  };
}

type RoomUniverseContent =
  | {
      readonly state: "selection";
      readonly coordinate: RoomUniverseCoordinate;
      readonly selection: WorldContextSelection;
      readonly sourceRevisions?: ContextSources;
      readonly pages: Readonly<Record<string, RoomUniversePageState>>;
      readonly directories: Readonly<Record<string, Readonly<Record<string, LocalBoundaryRead>>>>;
    }
  | {
      readonly state: "incomplete";
      readonly groups: ReadonlyArray<RoomUniverseGroup>;
      readonly reason?: string;
      readonly previous?: RoomUniverseSelectionEntry;
    }
  | {
      readonly state: "complete";
      readonly coordinate: RoomUniverseCoordinate;
      readonly groups: ReadonlyArray<RoomUniverseGroup>;
      readonly memberIds: ReadonlyArray<string>;
      readonly catalogDigest: string;
      readonly catalogCustodyDigest: string;
      /** Read-only projection of the World ledger at selectionCut. */
      readonly selection: WorldContextSelection;
    }
  | RoomUniversePagedEntry;

type RoomUniverseSelectionEntry = Extract<
  RoomUniverseContent,
  { readonly state: "complete" | "paged" | "selection" }
>;

/** A retained catalog is display-only: loading still refuses Send and moves. */
export type RoomUniverseEntry = (
  | { readonly state: "loading"; readonly previous?: RoomUniverseContent }
  | {
      readonly state: "refused";
      readonly reason: string;
      readonly previous?: RoomUniverseSelectionEntry;
    }
  | RoomUniverseContent
) & { readonly authorizationRevision?: number };

export function roomUniverseDisplayEntry(entry: RoomUniverseEntry | undefined) {
  return entry?.state === "loading" ? entry.previous : entry;
}

/** A failed source browse cannot erase the selection the person last saw. It
 * remains an expected cut, never permission to silently adopt another cut. */
export function roomUniverseKnownSelection(
  entry: RoomUniverseEntry | undefined,
): RoomUniverseSelectionEntry | undefined {
  if (entry?.state === "complete" || entry?.state === "paged" || entry?.state === "selection")
    return entry;
  return entry?.previous === undefined ? undefined : roomUniverseKnownSelection(entry.previous);
}

/**
 * RL-CAN-26: a large room held by its SUMMARY, never its complete arrays.
 * The catalog digests and the selection head (cut, digest) are the CAS every
 * page and predicate presents; the summary's bitsets carry every mark; the
 * pages cache holds only what the rail has shown. The exact selection
 * receipt is materialized at Send through the receipt-only read — frozen
 * boundaries stay exact while the display never downloads the catalog.
 */
export interface RoomUniversePagedEntry {
  readonly state: "paged";
  readonly coordinate: RoomUniverseCoordinate;
  readonly catalogDigest: string;
  readonly catalogCustodyDigest: string;
  readonly summary: RoomUniverseSummary;
  readonly selection: {
    readonly selectionCut: number;
    readonly selectionDigest: string;
  };
  readonly pages: Readonly<Record<string, RoomUniversePageState>>;
}

/**
 * The catalog-only coordinate: the CATALOG belongs to the room (CX15), so
 * a World tab fronting an ungoverned leaf reads it by (worldKey, roomId)
 * alone. These entries live in their own map — they carry no selection and no
 * CAS digests, so nothing here can ever seed a move.
 */
export interface RoomCatalogCoordinate {
  readonly worldKey: string;
  readonly roomId: string;
}

type RoomCatalogContent =
  | { readonly state: "incomplete"; readonly groups: ReadonlyArray<RoomUniverseGroup> }
  | {
      readonly state: "catalog";
      readonly groups: ReadonlyArray<RoomUniverseGroup>;
      readonly memberIds: ReadonlyArray<string>;
    };

export type RoomCatalogEntry = (
  | { readonly state: "loading"; readonly previous?: RoomCatalogContent }
  | { readonly state: "refused"; readonly reason: string }
  | RoomCatalogContent
) & { readonly authorizationRevision?: number };

export function roomCatalogDisplayEntry(entry: RoomCatalogEntry | undefined) {
  return entry?.state === "loading" ? entry.previous : entry;
}

export type CatalogSelection =
  | {
      readonly _tag: "Ready";
      readonly memberIds: ReadonlyArray<string>;
      readonly catalogDigest: string;
      readonly catalogCustodyDigest: string;
      readonly selection: WorldContextSelection;
    }
  | { readonly _tag: "Refused"; readonly reason: "catalog-unavailable" };

/** A stored World selection is exact without claiming a source catalog. */
export type MaterializedSelection =
  | CatalogSelection
  | {
      readonly _tag: "Ready";
      readonly memberIds: ReadonlyArray<string>;
      readonly selection: WorldContextSelection;
      readonly catalogDigest?: undefined;
      readonly catalogCustodyDigest?: undefined;
    };

interface RoomUniverseState {
  readonly byCoordinate: Readonly<Record<string, RoomUniverseEntry>>;
  readonly catalogByRoom: Readonly<Record<string, RoomCatalogEntry>>;
  refresh: (coordinate: RoomUniverseCoordinate, options?: RefreshOptions) => Promise<void>;
  refreshCatalog: (coordinate: RoomCatalogCoordinate, options?: RefreshOptions) => Promise<void>;
  moveMember: (
    input: RoomUniverseCoordinate & {
      readonly kind?: "member" | "folder";
      readonly memberId: string;
      readonly direction: "in" | "out";
      readonly delivery?: ContextDeliveryMode;
    },
  ) => Promise<RoomContextMoveOutcome>;
  /**
   * The atomic batch doorway (CB6 riding CB5): one POST commits the whole
   * gesture through one door call and one CAS — a refusal moves nothing, so
   * there is no partial landing to narrate.
   */
  moveMembers: (
    input: RoomUniverseCoordinate & {
      readonly moves: ReadonlyArray<{
        readonly kind?: "member" | "folder";
        readonly memberId: string;
        readonly direction: "in" | "out";
        readonly delivery?: ContextDeliveryMode;
      }>;
      readonly replace?: boolean;
      readonly clearGroups?: ReadonlyArray<string>;
    },
  ) => Promise<RoomContextMoveOutcome>;
  /**
   * RL-CAN-26: read one bounded page of a paged room — the rows the rail
   * shows for one group under one filter at one offset. Bound to the entry's
   * catalog digest and selection head; drift re-reads the summary.
   */
  loadPage: (coordinate: RoomUniverseCoordinate, request: RoomUniversePageRequest) => Promise<void>;
  loadDirectory: (
    coordinate: RoomUniverseCoordinate,
    group: "root-codebase" | "world-files",
    directory: string,
    cursor?: string,
  ) => Promise<void>;
  /**
   * RL-CAN-26: a group or filtered gesture the SERVER expands against the
   * same catalog revision into the exact CB6 batch — the browser names the
   * predicate, never a member list it does not hold.
   */
  movePredicate: (
    coordinate: RoomUniverseCoordinate,
    predicate: RoomUniverseSelectionPredicate,
  ) => Promise<RoomContextMoveOutcome>;
  /**
   * RL-CAN-26: the exact World receipt for a paged room, read once at Send
   * (receipt only — no catalog echo) so the frozen boundary stays exact.
   */
  materializeSelection: (coordinate: RoomUniverseCoordinate) => Promise<MaterializedSelection>;
}

/**
 * JB2: a move refusal carries the door's typed kind (context_changed is the
 * CAS state consumers switch on); "catalog-unavailable" is the client's own
 * pre-flight refusal, and receipt-integrity or transport failures normalize
 * to "transport". The reason stays a human sentence, never a switch surface.
 */
export type RoomContextMoveRefusalKind = WorldSessionDoorRefusalKind | "catalog-unavailable";

export type RoomContextMoveOutcome =
  | { readonly _tag: "Moved" }
  | {
      readonly _tag: "Refused";
      readonly kind: RoomContextMoveRefusalKind;
      readonly reason: string;
    };

export const roomUniverseCoordinateKey = (coordinate: RoomUniverseCoordinate): string =>
  [
    coordinate.worldKey,
    coordinate.roomId,
    coordinate.sessionId,
    coordinate.agentEnvironmentId,
    coordinate.agentThreadId,
  ].join("\u0000");

export const roomCatalogKey = (coordinate: RoomCatalogCoordinate): string =>
  [coordinate.worldKey, coordinate.roomId].join("\u0000");

interface RefreshOptions {
  /** Supersede an in-flight read whose inputs may predate a settings write. */
  readonly force?: boolean;
  /** An authorization change must never retain content from the old grant. */
  readonly authorizationRevision?: number;
  /** The consumer's observed inputs, including remote and local invalidations. */
  readonly readRevision?: string;
  /** Live revisions invalidate only the pages belonging to a changed source. */
  readonly sourceRevisions?: ContextSources;
  /** The watch's exact selection revision, independent of source invalidations. */
  readonly selectionRevision?: { readonly cut: number; readonly digest: string };
  /** Birth shares the addressed read before its watch has supplied a revision. */
  readonly shareSelection?: boolean;
  /** A post-write read may share an independently read receipt that has
   * advanced beyond the pre-write cut; the write response is never reused. */
  readonly afterSelectionCut?: number;
  /** Explicit local/source refreshes may name groups beyond the reactive sources. */
  readonly invalidateGroups?: readonly string[];
  /** A disposed observer may share a read, but may not publish its late result. */
  readonly isCurrent?: () => boolean;
}

const roomRefreshGenerations = new Map<string, number>();
const catalogRefreshGenerations = new Map<string, number>();

function contextPageInvalidated(pageKey: string, sources: ReadonlySet<string>) {
  const [group, filter] = JSON.parse(pageKey);
  if (sources.has(group)) return true;
  const kind = Array.isArray(filter) ? filter[2] : null;
  return (
    group === "world-vfs" &&
    sources.has("world-library") &&
    (kind === null ||
      kind === undefined ||
      ["media", "blueprint", "board-component"].includes(kind))
  );
}

/** Brief navigation reuse; a revision change or explicit refresh bypasses it. */
export const ROOM_UNIVERSE_RETAIN_MS = 30_000;
interface SharedRefresh {
  readonly authorizationRevision: number | undefined;
  readonly readRevision: string | undefined;
  readonly consumers: Array<() => boolean>;
  readonly isAuthorized: () => boolean;
  entry: RoomUniverseEntry | RoomCatalogEntry | undefined;
  promise: Promise<void>;
}
interface RetainedRefresh {
  readonly authorizationRevision: number | undefined;
  readonly readRevision: string | undefined;
  readonly entry: RoomUniverseEntry | RoomCatalogEntry;
  readonly readAt: number;
  readonly isAuthorized: () => boolean;
}
const roomPendingRefreshes = new Map<string, SharedRefresh>();
const catalogPendingRefreshes = new Map<string, SharedRefresh>();
const roomRetainedRefreshes = new Map<string, RetainedRefresh>();
const catalogRetainedRefreshes = new Map<string, RetainedRefresh>();

function currentReadAuthorization(key: string): () => boolean {
  const before = useNeucleosWorldStore.getState();
  const worldKey = key.split("\u0000")[0];
  const grant = before.worlds.find((world) => world.worldKey === worldKey);
  return () => {
    const now = useNeucleosWorldStore.getState();
    return (
      now.identity === before.identity &&
      now.signedIn === before.signedIn &&
      now.grantRevision === before.grantRevision &&
      // A non-renderer caller can use the authenticated door before the public
      // status has loaded. Once loaded, the current World grant is mandatory.
      (!now.loaded ||
        (now.signedIn && grant?.mcpTokenPresent === true && grant.expiresAt > Date.now()))
    );
  };
}

function shareRefresh(
  pending: Map<string, SharedRefresh>,
  retained: Map<string, RetainedRefresh>,
  key: string,
  authorizationRevision: number | undefined,
  options: RefreshOptions | undefined,
  peek: () => RoomUniverseEntry | RoomCatalogEntry | undefined,
  read: (mayPublish: () => boolean) => Promise<void>,
): Promise<void> {
  const current = peek();
  const isCurrent = options?.isCurrent ?? (() => true);
  const running = pending.get(key);
  if (
    options?.force !== true &&
    running &&
    running.entry === current &&
    running.authorizationRevision === authorizationRevision &&
    running.readRevision === options?.readRevision &&
    running.isAuthorized()
  ) {
    running.consumers.push(isCurrent);
    return running.promise;
  }
  const cached = retained.get(key);
  if (
    options?.force !== true &&
    options?.readRevision !== undefined &&
    cached?.readRevision === options.readRevision &&
    cached.authorizationRevision === authorizationRevision &&
    cached.entry === current &&
    Date.now() - cached.readAt < ROOM_UNIVERSE_RETAIN_MS &&
    cached.isAuthorized() &&
    isCurrent()
  )
    return Promise.resolve();
  retained.delete(key);
  const isAuthorized = currentReadAuthorization(key);
  if (!isAuthorized()) return Promise.resolve();
  const shared: SharedRefresh = {
    authorizationRevision,
    readRevision: options?.readRevision,
    consumers: [isCurrent],
    isAuthorized,
    entry: undefined,
    promise: Promise.resolve(),
  };
  pending.set(key, shared);
  const mayPublish = () =>
    pending.get(key) === shared &&
    shared.isAuthorized() &&
    shared.consumers.some((consumer) => consumer());
  shared.promise = read(mayPublish)
    .then(() => {
      const entry = peek();
      if (
        mayPublish() &&
        entry &&
        (options?.readRevision !== undefined || entry.state === "selection") &&
        (entry.state === "selection" ||
          entry.state === "catalog" ||
          entry.state === "paged" ||
          entry.state === "complete")
      )
        retained.set(key, {
          authorizationRevision,
          readRevision: options?.readRevision,
          entry,
          readAt: Date.now(),
          isAuthorized,
        });
    })
    .finally(() => {
      if (pending.get(key) === shared) pending.delete(key);
    });
  shared.entry = peek();
  return shared.promise;
}

function nextGeneration(generations: Map<string, number>, key: string): number {
  const generation = (generations.get(key) ?? 0) + 1;
  generations.set(key, generation);
  return generation;
}

function parseRoomUniverseCoordinateKey(key: string): RoomUniverseCoordinate | null {
  const parts = key.split("\u0000");
  if (parts.length !== 5) return null;
  const [worldKey, roomId, sessionId, agentEnvironmentId, agentThreadId] = parts;
  if (
    worldKey === undefined ||
    roomId === undefined ||
    sessionId === undefined ||
    agentEnvironmentId === undefined ||
    agentThreadId === undefined
  ) {
    return null;
  }
  return { worldKey, roomId, sessionId, agentEnvironmentId, agentThreadId };
}

function parseRoomCatalogKey(key: string): RoomCatalogCoordinate | null {
  const parts = key.split("\u0000");
  if (parts.length !== 2) return null;
  const [worldKey, roomId] = parts;
  return worldKey === undefined || roomId === undefined ? null : { worldKey, roomId };
}

const SHA256_HEX = /^[0-9a-f]{64}$/u;

/** A receipt is valid when it names this coordinate and still digests to its own body. */
async function validSelectionReceipt(
  selection: WorldContextSelection,
  coordinate: RoomUniverseCoordinate,
): Promise<boolean> {
  if (
    !sameCoordinate(selection, coordinate) ||
    selection.schemaVersion !== WORLD_SESSION_SELECTION_SCHEMA_VERSION ||
    !SHA256_HEX.test(selection.selectionDigest) ||
    !Number.isSafeInteger(selection.selectionCut) ||
    selection.selectionCut < 0 ||
    !Array.isArray(selection.memberIds) ||
    new Set(selection.memberIds).size !== selection.memberIds.length ||
    workingContextMemberIds(selection) === null
  ) {
    return false;
  }
  const members =
    selection.memberManifest === undefined
      ? { memberIds: selection.memberIds }
      : { memberManifest: selection.memberManifest };
  return (
    digestSessionSelection(selection.worldKey, selection.sessionId, selection.selectionCut, {
      ...members,
      ...(selection.expressions === undefined ? {} : { expressions: selection.expressions }),
      ...(selection.workingContextBitmap === undefined
        ? {}
        : { workingContextBitmap: selection.workingContextBitmap }),
    }) === selection.selectionDigest
  );
}

function sameCoordinate(left: WorldContextSelection, right: RoomUniverseCoordinate): boolean {
  return (
    left.worldKey === right.worldKey &&
    left.roomId === right.roomId &&
    left.sessionId === right.sessionId &&
    left.agentEnvironmentId === right.agentEnvironmentId &&
    left.agentThreadId === right.agentThreadId
  );
}

async function jsonBody(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await response.json();
    return typeof body === "object" && body !== null && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function refusalReason(body: Record<string, unknown> | null, fallback: string): string {
  return typeof body?.["reason"] === "string" ? body["reason"] : fallback;
}

/**
 * A door refusal carries its typed kind; a body without one (malformed, or a
 * valid-looking body whose receipt failed integrity) is the client's own
 * transport-class refusal.
 */
function refusalKindOf(body: Record<string, unknown> | null): WorldSessionDoorRefusalKind {
  return typeof body?.["kind"] === "string"
    ? normalizeWorldSessionDoorRefusalKind(body["kind"])
    : "transport";
}

function orderedSelectionMove(
  coordinate: RoomUniverseCoordinate,
  get: () => RoomUniverseState,
  commit: (order: SelectionOperation) => Promise<RoomContextMoveOutcome>,
): Promise<RoomContextMoveOutcome> {
  const shown = get().byCoordinate[roomUniverseCoordinateKey(coordinate)];
  const displayed =
    shown?.state === "complete" || shown?.state === "paged" || shown?.state === "selection"
      ? shown.selection
      : undefined;
  return orderSessionSelection(coordinate, displayed, async (order) => {
    if (order.failed)
      return {
        _tag: "Refused",
        kind: "context_changed",
        reason: "A preceding selection gesture failed. Review the selection and retry.",
      };
    const result = await commit(order);
    if (result._tag === "Refused") order.refuse();
    return result;
  });
}

export const useRoomUniverseStore = create<RoomUniverseState>((set, get) => ({
  byCoordinate: {},
  catalogByRoom: {},
  refreshCatalog: async (coordinate, options) => {
    const key = roomCatalogKey(coordinate);
    const current = get().catalogByRoom[key];
    const authorizationRevision = options?.authorizationRevision ?? current?.authorizationRevision;
    return shareRefresh(
      catalogPendingRefreshes,
      catalogRetainedRefreshes,
      key,
      authorizationRevision,
      options,
      () => get().catalogByRoom[key],
      async (mayPublish) => {
        const authority = authorizationRevision === undefined ? {} : { authorizationRevision };
        const visible = roomCatalogDisplayEntry(current);
        const previous =
          current?.authorizationRevision === authorizationRevision &&
          visible !== undefined &&
          visible.state !== "refused"
            ? visible
            : undefined;
        const generation = nextGeneration(catalogRefreshGenerations, key);
        set((state) => ({
          catalogByRoom: {
            ...state.catalogByRoom,
            [key]: {
              state: "loading",
              ...authority,
              ...(previous === undefined ? {} : { previous }),
            },
          },
        }));
        let entry: RoomCatalogEntry;
        try {
          const response = await fetchPrimary("/api/neucleos/room-universe", {
            method: "POST",
            headers: { "content-type": "application/json" },
            credentials: "include",
            body: JSON.stringify(coordinate),
          });
          const body = await jsonBody(response);
          const universe = body?.["universe"] as Record<string, unknown> | undefined;
          const memberIds = universe?.["memberIds"];
          const groups = universe?.["groups"];
          if (!response.ok || body?.["ok"] !== true || universe === undefined) {
            entry = {
              state: "refused",
              reason: refusalReason(body, "The room catalog read was refused."),
            };
          } else if (
            universe["state"] === "catalog" &&
            Array.isArray(groups) &&
            Array.isArray(memberIds) &&
            memberIds.every((memberId) => typeof memberId === "string")
          ) {
            entry = {
              state: "catalog",
              groups: groups as ReadonlyArray<RoomUniverseGroup>,
              memberIds: memberIds as ReadonlyArray<string>,
            };
          } else if (universe["state"] === "incomplete" && Array.isArray(groups)) {
            entry = { state: "incomplete", groups: groups as ReadonlyArray<RoomUniverseGroup> };
          } else {
            entry = { state: "refused", reason: "The room catalog readback is invalid." };
          }
        } catch {
          entry = { state: "refused", reason: "The local server was unreachable." };
        }
        if (catalogRefreshGenerations.get(key) === generation && mayPublish()) {
          set((state) => ({
            catalogByRoom: { ...state.catalogByRoom, [key]: { ...entry, ...authority } },
          }));
        }
      },
    );
  },
  refresh: async (coordinate, options) => {
    const key = roomUniverseCoordinateKey(coordinate);
    const current = get().byCoordinate[key];
    const authorizationRevision = options?.authorizationRevision ?? current?.authorizationRevision;
    const previousRevision = roomRetainedRefreshes.get(key)?.readRevision;
    const visible = roomUniverseDisplayEntry(current);
    const previousSources = visible?.state === "selection" ? visible.sourceRevisions : undefined;
    const sourceRevisions = options?.sourceRevisions ?? previousSources;
    const isCurrent = options?.isCurrent ?? (() => true);
    const isAuthorized = currentReadAuthorization(key);
    // Birth and the rail can observe the same independent read before they
    // have the same watch stamp. Share only verified receipts; a newer watch
    // cut, changed source, or explicit refresh still requires another read.
    const canShareSelection =
      (options?.force !== true || options.afterSelectionCut !== undefined) &&
      (options?.shareSelection === true || options?.selectionRevision !== undefined) &&
      !options?.invalidateGroups?.length &&
      (!options?.sourceRevisions ||
        !previousSources ||
        Object.keys(options.sourceRevisions).every(
          (group) =>
            options.sourceRevisions?.[group as keyof ContextSources] ===
            previousSources[group as keyof ContextSources],
        ));
    if (canShareSelection) {
      const running = roomPendingRefreshes.get(key);
      if (
        running &&
        running.entry === current &&
        running.authorizationRevision === authorizationRevision &&
        running.isAuthorized() &&
        (options?.shareSelection === true || running.readRevision === undefined)
      ) {
        running.consumers.push(isCurrent);
        await running.promise;
        if (!isAuthorized() || !isCurrent()) return;
        return get().refresh(coordinate, options);
      }
      const cached = roomRetainedRefreshes.get(key);
      const observed = options?.selectionRevision;
      if (
        cached &&
        cached.entry === current &&
        cached.authorizationRevision === authorizationRevision &&
        current?.state === "selection" &&
        Date.now() - cached.readAt < ROOM_UNIVERSE_RETAIN_MS &&
        cached.isAuthorized() &&
        isCurrent() &&
        (options?.afterSelectionCut === undefined ||
          current.selection.selectionCut > options.afterSelectionCut) &&
        (observed === undefined ||
          current.selection.selectionCut > observed.cut ||
          (current.selection.selectionCut === observed.cut &&
            current.selection.selectionDigest === observed.digest))
      ) {
        const entry =
          options?.sourceRevisions && previousSources === undefined
            ? { ...current, sourceRevisions: options.sourceRevisions }
            : current;
        if (entry !== current)
          set((state) => ({ byCoordinate: { ...state.byCoordinate, [key]: entry } }));
        roomRetainedRefreshes.set(key, {
          ...cached,
          entry,
          readRevision: options?.readRevision ?? cached.readRevision,
        });
        return;
      }
    }
    const invalidateGroups = new Set(
      options?.invalidateGroups ??
        (options?.sourceRevisions
          ? Object.keys(options.sourceRevisions).filter(
              (group) =>
                options.sourceRevisions?.[group as keyof ContextSources] !==
                previousSources?.[group as keyof ContextSources],
            )
          : options?.force === true ||
              (options?.readRevision !== undefined && options.readRevision !== previousRevision)
            ? ["world-convex"]
            : []),
    );
    return shareRefresh(
      roomPendingRefreshes,
      roomRetainedRefreshes,
      key,
      authorizationRevision,
      options,
      () => get().byCoordinate[key],
      async (mayPublish) => {
        const authority = authorizationRevision === undefined ? {} : { authorizationRevision };
        const visible = roomUniverseDisplayEntry(current);
        const previous =
          current?.authorizationRevision !== authorizationRevision
            ? undefined
            : visible !== undefined && visible.state !== "refused"
              ? visible
              : roomUniverseKnownSelection(current);
        const generation = nextGeneration(roomRefreshGenerations, key);
        set((state) => ({
          byCoordinate: {
            ...state.byCoordinate,
            [key]: {
              state: "loading",
              ...authority,
              ...(previous === undefined ? {} : { previous }),
            },
          },
        }));
        let entry: RoomUniverseEntry;
        try {
          const response = await fetchPrimary("/api/neucleos/context-selection", {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(coordinate),
          });
          const body = await jsonBody(response);
          const selection = body?.["selection"] as WorldContextSelection | undefined;
          entry =
            response.ok &&
            body?.["ok"] === true &&
            selection &&
            (await validSelectionReceipt(selection, coordinate))
              ? {
                  state: "selection",
                  coordinate,
                  selection,
                  ...(sourceRevisions ? { sourceRevisions } : {}),
                  pages:
                    previous?.state === "selection"
                      ? Object.fromEntries(
                          Object.entries(previous.pages).filter(
                            ([pageKey, page]) =>
                              page.state === "page" &&
                              !contextPageInvalidated(pageKey, invalidateGroups),
                          ),
                        )
                      : {},
                  directories:
                    previous?.state === "selection"
                      ? Object.fromEntries(
                          Object.entries(previous.directories).filter(
                            ([group]) => !invalidateGroups.has(group),
                          ),
                        )
                      : {},
                }
              : {
                  state: "refused",
                  reason: refusalReason(body, "The World selection read was refused."),
                };
        } catch {
          entry = { state: "refused", reason: "The World selection read was unreachable." };
        }
        if (roomRefreshGenerations.get(key) === generation && mayPublish()) {
          set((state) => {
            const known = roomUniverseKnownSelection(state.byCoordinate[key]);
            const retained =
              entry.state === "refused" && known
                ? { ...entry, previous: known }
                : entry.state === "selection" &&
                    known &&
                    known.selection.selectionCut > entry.selection.selectionCut
                  ? known
                  : entry;
            return {
              byCoordinate: { ...state.byCoordinate, [key]: { ...retained, ...authority } },
            };
          });
        }
      },
    );
  },
  moveMember: (input) =>
    orderedSelectionMove(input, get, async (order) => {
      const key = roomUniverseCoordinateKey(input);
      const current = get().byCoordinate[key];
      if (current?.state === "paged") {
        return commitPagedMoves(set, get, current, order, [
          {
            ...(input.kind ? { kind: input.kind } : {}),
            memberId: input.memberId,
            direction: input.direction,
            ...(input.delivery === undefined ? {} : { delivery: input.delivery }),
          },
        ]);
      }
      if (current?.state !== "complete" && current?.state !== "selection") {
        return { _tag: "Refused", kind: "catalog-unavailable", reason: "catalog-unavailable" };
      }
      try {
        const response = await fetchPrimary("/api/neucleos/context-selection/move", {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            ...input,
            ...("catalogDigest" in current ? { expectedCatalogDigest: current.catalogDigest } : {}),
            expectedSelectionCut: order.expected?.selectionCut ?? current.selection.selectionCut,
            expectedSelectionDigest:
              order.expected?.selectionDigest ?? current.selection.selectionDigest,
          }),
        });
        const body = await jsonBody(response);
        const selection = body?.["selection"] as WorldContextSelection | undefined;
        const selectionValid =
          selection !== undefined &&
          selection.selectionCut > current.selection.selectionCut &&
          (await validSelectionReceipt(selection, input));
        if (!response.ok || body?.["ok"] !== true || selection === undefined || !selectionValid) {
          if (body?.["kind"] === "context_changed" || body?.["kind"] === "selection_changed") {
            await get().refresh(current.coordinate, { force: true });
          }
          return {
            _tag: "Refused",
            kind: refusalKindOf(body),
            reason: refusalReason(body, "The World refused the Context move."),
          };
        }
        const observed = get().byCoordinate[key];
        // A live refresh can overlap this gesture's successful response. Its
        // retained content is still usable under the same authority; loading
        // alone must not turn an accepted move into an access refusal.
        const latest = observed?.state === "loading" ? observed.previous : observed;
        if (
          (latest?.state !== "complete" && latest?.state !== "selection") ||
          observed?.authorizationRevision !== current.authorizationRevision
        )
          return {
            _tag: "Refused",
            kind: "context_changed",
            reason: "World access changed during this gesture. Review the selection.",
          };
        set((state) => ({
          byCoordinate: {
            ...state.byCoordinate,
            [key]: {
              ...latest,
              ...(observed?.authorizationRevision === undefined
                ? {}
                : { authorizationRevision: observed.authorizationRevision }),
              selection,
              ...(latest.state === "selection"
                ? {
                    pages: Object.fromEntries(
                      Object.entries(latest.pages).filter(([, page]) => page.state === "page"),
                    ),
                  }
                : {}),
            },
          },
        }));
        if (
          typeof body?.["committedSelectionCut"] === "number" &&
          body["committedSelectionCut"] !== selection.selectionCut
        )
          return {
            _tag: "Refused",
            kind: "context_changed",
            reason:
              "The gesture committed, then another client changed the selection. Review it before sending.",
          };
        order.accept(selection);
        return { _tag: "Moved" };
      } catch {
        return { _tag: "Refused", kind: "transport", reason: "The local server was unreachable." };
      }
    }),
  moveMembers: (input) =>
    orderedSelectionMove(input, get, async (order) => {
      const key = roomUniverseCoordinateKey(input);
      const current = get().byCoordinate[key];
      if (current?.state === "paged")
        return commitPagedMoves(set, get, current, order, input.moves, input);
      if (current?.state !== "complete" && current?.state !== "selection") {
        return { _tag: "Refused", kind: "catalog-unavailable", reason: "catalog-unavailable" };
      }
      if (input.moves.length === 0 && !input.replace && !input.clearGroups?.length)
        return { _tag: "Moved" };
      try {
        const response = await fetchPrimary("/api/neucleos/context-selection/move-batch", {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            worldKey: input.worldKey,
            roomId: input.roomId,
            sessionId: input.sessionId,
            agentEnvironmentId: input.agentEnvironmentId,
            agentThreadId: input.agentThreadId,
            moves: input.moves,
            ...(input.replace ? { replace: true } : {}),
            ...(input.clearGroups ? { clearGroups: input.clearGroups } : {}),
            ...("catalogDigest" in current ? { expectedCatalogDigest: current.catalogDigest } : {}),
            expectedSelectionCut: order.expected?.selectionCut ?? current.selection.selectionCut,
            expectedSelectionDigest:
              order.expected?.selectionDigest ?? current.selection.selectionDigest,
          }),
        });
        const body = await jsonBody(response);
        const selection = body?.["selection"] as WorldContextSelection | undefined;
        const selectionValid =
          selection !== undefined &&
          selection.selectionCut > current.selection.selectionCut &&
          (await validSelectionReceipt(selection, input));
        if (!response.ok || body?.["ok"] !== true || selection === undefined || !selectionValid) {
          if (body?.["kind"] === "context_changed" || body?.["kind"] === "selection_changed") {
            await get().refresh(current.coordinate, { force: true });
          }
          return {
            _tag: "Refused",
            kind: refusalKindOf(body),
            reason: refusalReason(body, "The World refused the Context batch."),
          };
        }
        const observed = get().byCoordinate[key];
        // A live refresh can overlap this gesture's successful response. Its
        // retained content is still usable under the same authority; loading
        // alone must not turn an accepted move into an access refusal.
        const latest = observed?.state === "loading" ? observed.previous : observed;
        if (
          (latest?.state !== "complete" && latest?.state !== "selection") ||
          observed?.authorizationRevision !== current.authorizationRevision
        )
          return {
            _tag: "Refused",
            kind: "context_changed",
            reason: "World access changed during this gesture. Review the selection.",
          };
        set((state) => ({
          byCoordinate: {
            ...state.byCoordinate,
            [key]: {
              ...latest,
              ...(observed?.authorizationRevision === undefined
                ? {}
                : { authorizationRevision: observed.authorizationRevision }),
              selection,
              ...(latest.state === "selection"
                ? {
                    pages: Object.fromEntries(
                      Object.entries(latest.pages).filter(([, page]) => page.state === "page"),
                    ),
                  }
                : {}),
            },
          },
        }));
        if (
          typeof body?.["committedSelectionCut"] === "number" &&
          body["committedSelectionCut"] !== selection.selectionCut
        )
          return {
            _tag: "Refused",
            kind: "context_changed",
            reason:
              "The gesture committed, then another client changed the selection. Review it before sending.",
          };
        order.accept(selection);
        return { _tag: "Moved" };
      } catch {
        return { _tag: "Refused", kind: "transport", reason: "The local server was unreachable." };
      }
    }),
  loadDirectory: async (coordinate, group, directory, cursor) => {
    const key = roomUniverseCoordinateKey(coordinate);
    // A selection read temporarily wraps the catalog in `loading`. Keep the
    // folder response bound to this address, and publish after that read has
    // settled so it cannot strand the retained page in its loading state.
    const settleSelection = async () => {
      while (roomPendingRefreshes.has(key)) await roomPendingRefreshes.get(key)!.promise;
    };
    if (roomPendingRefreshes.has(key)) await settleSelection();
    const current = get().byCoordinate[key];
    if (current?.state !== "selection") return;
    const before = current.directories[group]?.[directory];
    if (before?.state === "loading") return;
    const setPage = (page: LocalBoundaryRead) =>
      set((state) => {
        const entry = state.byCoordinate[key];
        if (
          entry?.state !== "selection" ||
          entry.authorizationRevision !== current.authorizationRevision
        )
          return state;
        const pages = entry.directories[group] ?? {};
        const refreshed = refreshLocalBoundaryLabels(
          pages[directory]?.state === "loading" && before
            ? { ...pages, [directory]: before }
            : pages,
          page.state === "members" ? page.members : [],
        );
        return {
          byCoordinate: {
            ...state.byCoordinate,
            [key]: {
              ...entry,
              directories: { ...entry.directories, [group]: { ...refreshed, [directory]: page } },
            },
          },
        };
      });
    const read = async (extra: Record<string, unknown>) => {
      const response = await fetchPrimary("/api/neucleos/room-universe/page", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...coordinate, group, listing: "folder", ...extra }),
      });
      const body = await jsonBody(response);
      if (!response.ok || body?.["ok"] !== true)
        throw new Error(refusalReason(body, "The World folder read was refused."));
      return projectLocalBoundaryResponse(body["directory"]);
    };
    setPage({ state: "loading" });
    try {
      const page = await read({ folder: directory, ...(cursor ? { cursor } : {}) });
      await settleSelection();
      setPage(
        page.state === "members" && cursor && before?.state === "members"
          ? { ...page, members: [...before.members, ...page.members] }
          : page,
      );
      if (!directory && !cursor) {
        const references =
          current.selection.expressions?.flatMap((choice) => {
            const ref = parseFilesystemMemberReference(choice.memberId);
            return ref?.group === group ? [ref.relativePath] : [];
          }) ?? [];
        for (let offset = 0; offset < references.length; offset += 128) {
          const named = await read({ references: references.slice(offset, offset + 128) }).catch(
            () => null,
          );
          await settleSelection();
          const entry = get().byCoordinate[key];
          if (
            entry?.state !== "selection" ||
            entry.authorizationRevision !== current.authorizationRevision
          )
            return;
          const rootPage = entry.directories[group]?.[""];
          if (named?.state === "members" && rootPage?.state === "members")
            setPage({ ...rootPage, members: [...rootPage.members, ...named.members] });
        }
      }
    } catch (error) {
      await settleSelection();
      setPage({
        state: "unavailable",
        reason: error instanceof Error ? error.message : "The World folder could not be read.",
      });
    }
  },
  loadPage: async (coordinate, request) => {
    const key = roomUniverseCoordinateKey(coordinate);
    let current = get().byCoordinate[key];
    if (request.refresh && (current?.state === "paged" || current?.state === "selection")) {
      const refreshed = {
        ...current,
        pages: Object.fromEntries(
          Object.entries(current.pages).filter(
            ([pageKey]) => JSON.parse(pageKey)[0] !== request.group,
          ),
        ),
      };
      set((state) => ({ byCoordinate: { ...state.byCoordinate, [key]: refreshed } }));
      current = refreshed;
    }
    if (current?.state !== "paged" && current?.state !== "selection") return;
    const pageKey = roomUniversePageKey(request);
    if (current.pages[pageKey] !== undefined) return;
    const stamp = current.selection.selectionDigest;
    const authorizationRevision = current.authorizationRevision;
    const loadingPage = { state: "loading" as const };
    const setPage = (page: RoomUniversePageState) =>
      set((state) => {
        const entry = state.byCoordinate[key];
        // Only the entry the request was bound to may receive its answer.
        if (
          (entry?.state !== "paged" && entry?.state !== "selection") ||
          entry.selection.selectionDigest !== stamp ||
          entry.authorizationRevision !== authorizationRevision ||
          (page !== loadingPage && entry.pages[pageKey] !== loadingPage)
        )
          return state;
        return {
          byCoordinate: {
            ...state.byCoordinate,
            [key]: { ...entry, pages: { ...entry.pages, [pageKey]: page } },
          },
        };
      });
    setPage(loadingPage);
    const filter = normalizePageFilter(request.filter);
    try {
      const response = await fetchPrimary("/api/neucleos/room-universe/page", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...coordinate,
          group: request.group,
          ...(request.cursor === undefined ? {} : { cursor: request.cursor }),
          offset: request.offset ?? 0,
          limit: ROOM_UNIVERSE_PAGE_MAX,
          ...(filter === undefined ? {} : { filter }),
          ...("catalogDigest" in current ? { expectedCatalogDigest: current.catalogDigest } : {}),
          expectedSelectionCut: current.selection.selectionCut,
          expectedSelectionDigest: current.selection.selectionDigest,
        }),
      });
      const body = await jsonBody(response);
      const page = parseRoomUniversePage(body?.["page"]);
      if (response.ok && body?.["ok"] === true && page !== null) {
        setPage({ state: "page", page });
        return;
      }
      const kind = body?.["kind"];
      if (kind === "context_changed" || kind === "selection_changed") {
        // Drift: the room moved under the page. The summary re-reads; every
        // page bound to the old stamp is dropped with it.
        void get().refresh(coordinate, { force: true });
        return;
      }
      setPage({ state: "refused", reason: refusalReason(body, "The page read was refused.") });
    } catch {
      setPage({ state: "refused", reason: "The local server was unreachable." });
    }
  },
  movePredicate: (coordinate, predicate) =>
    orderedSelectionMove(coordinate, get, async (order) => {
      const key = roomUniverseCoordinateKey(coordinate);
      const current = get().byCoordinate[key];
      if (current?.state !== "paged") {
        return { _tag: "Refused", kind: "catalog-unavailable", reason: "catalog-unavailable" };
      }
      const filter = normalizePageFilter(predicate.filter);
      try {
        const response = await fetchPrimary("/api/neucleos/context-selection/move-predicate", {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            ...coordinate,
            predicate: {
              group: predicate.group,
              gesture: predicate.gesture,
              ...(filter === undefined ? {} : { filter }),
            },
            ...("catalogDigest" in current ? { expectedCatalogDigest: current.catalogDigest } : {}),
            expectedSelectionCut: order.expected?.selectionCut ?? current.selection.selectionCut,
            expectedSelectionDigest:
              order.expected?.selectionDigest ?? current.selection.selectionDigest,
          }),
        });
        const body = await jsonBody(response);
        const summary = parseRoomUniverseSummary(body?.["summary"]);
        const head = selectionHeadOf(body?.["selection"]);
        if (
          !response.ok ||
          body?.["ok"] !== true ||
          body["catalogDigest"] !== current.catalogDigest ||
          summary === null ||
          head === null ||
          head.selectionCut <= current.selection.selectionCut
        ) {
          if (body?.["kind"] === "context_changed" || body?.["kind"] === "selection_changed") {
            await get().refresh(coordinate, { force: true });
          }
          return {
            _tag: "Refused",
            kind: refusalKindOf(body),
            reason: refusalReason(body, "The World refused the Context gesture."),
          };
        }
        set((state) => ({
          byCoordinate: {
            ...state.byCoordinate,
            [key]: { ...current, summary, selection: head, pages: {} },
          },
        }));
        if (
          typeof body?.["committedSelectionCut"] === "number" &&
          body["committedSelectionCut"] !== head.selectionCut
        )
          return {
            _tag: "Refused",
            kind: "context_changed",
            reason:
              "The gesture committed, then another client changed the selection. Review it before sending.",
          };
        order.accept(head);
        return { _tag: "Moved" };
      } catch {
        return { _tag: "Refused", kind: "transport", reason: "The local server was unreachable." };
      }
    }),
  materializeSelection: async (coordinate) => {
    const key = roomUniverseCoordinateKey(coordinate);
    const current = get().byCoordinate[key];
    if (current?.state === "selection") {
      const isAuthorized = currentReadAuthorization(key);
      if (
        !isAuthorized() ||
        roomUniverseCoordinateKey(current.coordinate) !== key ||
        (current.authorizationRevision !== undefined &&
          current.authorizationRevision !== useNeucleosWorldStore.getState().grantRevision) ||
        !(await validSelectionReceipt(current.selection, coordinate)) ||
        !isAuthorized() ||
        get().byCoordinate[key] !== current
      ) {
        return { _tag: "Refused", reason: "catalog-unavailable" };
      }
      return {
        _tag: "Ready",
        memberIds: current.selection.memberIds,
        selection: current.selection,
      };
    }
    if (current?.state !== "paged") return selectUniverseMemberIds(get().byCoordinate, key);
    try {
      const response = await fetchPrimary("/api/neucleos/context-selection", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...coordinate, receiptOnly: true }),
      });
      const body = await jsonBody(response);
      const selection = body?.["selection"] as WorldContextSelection | undefined;
      const valid =
        response.ok &&
        body?.["ok"] === true &&
        selection !== undefined &&
        (await validSelectionReceipt(selection, coordinate));
      if (!valid || selection === undefined)
        return { _tag: "Refused", reason: "catalog-unavailable" };
      if (
        selection.selectionCut !== current.selection.selectionCut ||
        selection.selectionDigest !== current.selection.selectionDigest
      ) {
        // The World moved past the summary the room shows: adopt the exact
        // receipt now (the freeze wants the World's truth) and re-fold.
        set((state) => {
          const entry = state.byCoordinate[key];
          return entry?.state !== "paged"
            ? state
            : {
                byCoordinate: {
                  ...state.byCoordinate,
                  [key]: {
                    ...entry,
                    selection: {
                      selectionCut: selection.selectionCut,
                      selectionDigest: selection.selectionDigest,
                    },
                    pages: {},
                  },
                },
              };
        });
        void get().refresh(coordinate, { force: true });
      }
      return {
        _tag: "Ready",
        // The receipt's members are the only catalog members a paged room
        // holds exactly; the receipt itself is what the freeze copies.
        memberIds: selection.memberIds,
        catalogDigest: current.catalogDigest,
        catalogCustodyDigest: current.catalogCustodyDigest,
        selection,
      };
    } catch {
      return { _tag: "Refused", reason: "catalog-unavailable" };
    }
  },
}));

/** Read through the rail's owner, then validate the complete birth receipt.
 * Post-write callers require an independent read beyond their previous cut. */
export async function readRoomUniverseSelection(
  coordinate: RoomUniverseCoordinate,
  options: Pick<
    RefreshOptions,
    "force" | "afterSelectionCut" | "authorizationRevision" | "isCurrent"
  > = {},
): Promise<
  | { readonly ok: true; readonly selection: WorldSessionSelection }
  | { readonly ok: false; readonly reason: string }
> {
  const key = roomUniverseCoordinateKey(coordinate);
  const isAuthorized = currentReadAuthorization(key);
  const isCurrent = () => isAuthorized() && (options.isCurrent?.() ?? true);
  const refused = {
    ok: false as const,
    reason: "The session's stored selection could not be read.",
  };
  if (!isCurrent()) return refused;
  await useRoomUniverseStore.getState().refresh(coordinate, {
    ...options,
    authorizationRevision:
      options.authorizationRevision ?? useNeucleosWorldStore.getState().grantRevision,
    shareSelection: true,
    isCurrent,
  });
  // A newer read may supersede this one while another consumer navigates.
  while (isCurrent() && roomPendingRefreshes.has(key)) await roomPendingRefreshes.get(key)!.promise;
  const entry = useRoomUniverseStore.getState().byCoordinate[key];
  if (!isCurrent() || entry?.state !== "selection")
    return entry?.state === "refused" ? { ok: false, reason: entry.reason } : refused;
  const parsed = parseWorldSessionSelectionReceipt(
    {
      ...entry.selection,
      count: entry.selection.memberIds.length,
      ...(entry.selection.expressions
        ? { expressionCount: entry.selection.expressions.length }
        : {}),
    },
    coordinate,
  );
  return parsed?.kind === "inline" ? { ok: true, selection: parsed.selection } : refused;
}

/** Public grant changes and expiry remove both retained data and pending publication. */
export function invalidateRoomUniverseAccess(worldKey?: string): void {
  const addressed = (key: string) => worldKey === undefined || key.split("\u0000")[0] === worldKey;
  for (const reads of [
    roomPendingRefreshes,
    catalogPendingRefreshes,
    roomRetainedRefreshes,
    catalogRetainedRefreshes,
  ])
    for (const key of reads.keys()) if (addressed(key)) reads.delete(key);
  useRoomUniverseStore.setState((state) => ({
    byCoordinate: Object.fromEntries(
      Object.entries(state.byCoordinate).filter(([key]) => !addressed(key)),
    ),
    catalogByRoom: Object.fromEntries(
      Object.entries(state.catalogByRoom).filter(([key]) => !addressed(key)),
    ),
  }));
}

useNeucleosWorldStore.subscribe((state, previous) => {
  if (
    state.grantRevision !== previous.grantRevision ||
    state.identity !== previous.identity ||
    state.signedIn !== previous.signedIn
  )
    invalidateRoomUniverseAccess();
});

// ---------------------------------------------------------------------------
// RL-CAN-26 helpers
// ---------------------------------------------------------------------------
function selectionHeadOf(
  value: unknown,
): { readonly selectionCut: number; readonly selectionDigest: string } | null {
  if (typeof value !== "object" || value === null) return null;
  const head = value as Record<string, unknown>;
  const cut = head["selectionCut"];
  const digest = head["selectionDigest"];
  return Number.isSafeInteger(cut) &&
    (cut as number) >= 0 &&
    typeof digest === "string" &&
    SHA256_HEX.test(digest)
    ? { selectionCut: cut as number, selectionDigest: digest }
    : null;
}

/**
 * One summary read. Null when the door is absent (an older server), refuses,
 * or answers anything but a complete paged universe — the caller then reads
 * the room the complete way, which reproduces every existing refusal.
 */
async function readPagedSummary(
  coordinate: RoomUniverseCoordinate,
): Promise<RoomUniversePagedEntry | null> {
  const response = await fetchPrimary("/api/neucleos/room-universe/summary", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(coordinate),
  });
  if (!response.ok) return null;
  const body = await jsonBody(response);
  const universe = body?.["universe"] as Record<string, unknown> | undefined;
  if (body?.["ok"] !== true || universe === undefined || universe["state"] !== "complete") {
    return null;
  }
  const summary = parseRoomUniverseSummary(universe["summary"]);
  const head = selectionHeadOf(universe["selection"]);
  const catalogDigest = universe["catalogDigest"];
  const catalogCustodyDigest = universe["catalogCustodyDigest"];
  if (
    summary === null ||
    head === null ||
    typeof catalogDigest !== "string" ||
    !SHA256_HEX.test(catalogDigest) ||
    typeof catalogCustodyDigest !== "string" ||
    !SHA256_HEX.test(catalogCustodyDigest)
  ) {
    return null;
  }
  return {
    state: "paged",
    coordinate,
    catalogDigest,
    catalogCustodyDigest,
    summary,
    selection: head,
    pages: {},
  };
}

/**
 * A member move against a paged room rides the SAME batch door as a complete
 * one, presenting the summary's CAS. The World's receipt is verified by
 * signature and digests (membership was the server's check — the browser
 * holds no catalog to repeat it), then the summary re-folds so every mark
 * the rail shows is the World's, never a local guess.
 */
async function validPagedMoveReceipt(
  selection: WorldContextSelection,
  coordinate: RoomUniverseCoordinate,
): Promise<boolean> {
  return validSelectionReceipt(selection, coordinate);
}

async function commitPagedMoves(
  set: (updater: (state: RoomUniverseState) => Partial<RoomUniverseState>) => void,
  get: () => RoomUniverseState,
  current: RoomUniversePagedEntry,
  order: SelectionOperation,
  moves: ReadonlyArray<{
    readonly kind?: "member" | "folder";
    readonly memberId: string;
    readonly direction: "in" | "out";
    readonly delivery?: ContextDeliveryMode;
  }>,
  options?: { readonly replace?: boolean; readonly clearGroups?: ReadonlyArray<string> },
): Promise<RoomContextMoveOutcome> {
  if (moves.length === 0 && !options?.replace && !options?.clearGroups?.length)
    return { _tag: "Moved" };
  const { coordinate } = current;
  const key = roomUniverseCoordinateKey(coordinate);
  try {
    const response = await fetchPrimary("/api/neucleos/context-selection/move-batch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        ...coordinate,
        moves,
        ...(options?.replace ? { replace: true } : {}),
        ...(options?.clearGroups ? { clearGroups: options.clearGroups } : {}),
        response: "head",
        ...("catalogDigest" in current ? { expectedCatalogDigest: current.catalogDigest } : {}),
        expectedSelectionCut: order.expected?.selectionCut ?? current.selection.selectionCut,
        expectedSelectionDigest:
          order.expected?.selectionDigest ?? current.selection.selectionDigest,
      }),
    });
    const body = await jsonBody(response);
    const selection = body?.["selection"] as WorldContextSelection | undefined;
    const selectionValid =
      selection !== undefined &&
      selection.selectionCut > current.selection.selectionCut &&
      (await validPagedMoveReceipt(selection, coordinate));
    if (!response.ok || body?.["ok"] !== true || selection === undefined || !selectionValid) {
      // The refused gesture wrote nothing. Replace the stale read so a
      // subsequent explicit gesture can use the current catalog and cut.
      if (body?.["kind"] === "context_changed" || body?.["kind"] === "selection_changed") {
        await get().refresh(coordinate, { force: true });
      }
      return {
        _tag: "Refused",
        kind: refusalKindOf(body),
        reason: refusalReason(body, "The World refused the Context move."),
      };
    }
    const refolded = await readPagedSummary(coordinate);
    set((state) => {
      const entry = state.byCoordinate[key];
      if (entry?.state !== "paged") return state;
      return {
        byCoordinate: {
          ...state.byCoordinate,
          [key]:
            refolded !== null && refolded.selection.selectionCut >= selection.selectionCut
              ? refolded
              : {
                  ...entry,
                  selection: {
                    selectionCut: selection.selectionCut,
                    selectionDigest: selection.selectionDigest,
                  },
                  pages: {},
                },
        },
      };
    });
    if (refolded === null) void get().refresh(coordinate, { force: true });
    if (
      typeof body?.["committedSelectionCut"] === "number" &&
      body["committedSelectionCut"] !== selection.selectionCut
    )
      return {
        _tag: "Refused",
        kind: "context_changed",
        reason:
          "The gesture committed, then another client changed the selection. Review it before sending.",
      };
    order.accept(selection);
    return { _tag: "Moved" };
  } catch {
    return { _tag: "Refused", kind: "transport", reason: "The local server was unreachable." };
  }
}

/**
 * A World-folder write changes the inputs to every cached catalog for that
 * World on this environment. Re-read those projections immediately; force
 * makes the post-write read supersede any request that began before the save.
 */
export async function refreshRoomUniversesForWorkspaceBinding(input: {
  readonly worldKey: string;
  readonly environmentId: string;
  readonly refreshCatalog: boolean;
}): Promise<void> {
  const state = useRoomUniverseStore.getState();
  const addressed = Object.keys(state.byCoordinate)
    .map(parseRoomUniverseCoordinateKey)
    .filter(
      (coordinate): coordinate is RoomUniverseCoordinate =>
        coordinate !== null &&
        coordinate.worldKey === input.worldKey &&
        coordinate.agentEnvironmentId === input.environmentId,
    );
  const catalogs = input.refreshCatalog
    ? Object.keys(state.catalogByRoom)
        .map(parseRoomCatalogKey)
        .filter(
          (coordinate): coordinate is RoomCatalogCoordinate =>
            coordinate !== null && coordinate.worldKey === input.worldKey,
        )
    : [];
  await Promise.all([
    ...addressed.map((coordinate) => state.refresh(coordinate, { force: true })),
    ...catalogs.map((coordinate) => state.refreshCatalog(coordinate, { force: true })),
  ]);
}

export type SendSelectionOutcome =
  | {
      readonly _tag: "Ready";
      readonly selection: WorldContextSelection;
      readonly resolution: AgentContextResolution;
    }
  | {
      readonly _tag: "Refused";
      readonly reason:
        | "selection-unavailable"
        | "selection-mismatch"
        | "source-unavailable"
        | "sign-in-ended";
      /** The World's own words for the refusal, when it gave them. */
      readonly detail?: string;
    };

/** WSET4: the message text Send preparation reads member ids from. */
const SEND_MESSAGE_TEXT_MAX = 65_536;

const ruleWords = (reason: string) =>
  reason === "rule:handle"
    ? "named in your message"
    : reason === "rule:transcript"
      ? "the conversation this one continues"
      : reason.startsWith("rule:tag:")
        ? `tagged “${reason.slice("rule:tag:".length)}”`
        : reason;

/** WSET4: say what the person's rules added to this message, and why. Never silent. */
function announceAutoAdded(autoTier: unknown) {
  const added = (autoTier as { added?: unknown } | undefined)?.added;
  if (!Array.isArray(added) || added.length === 0) return;
  const lines = added
    .filter(
      (item): item is { memberId: string; reason: string } =>
        typeof item?.memberId === "string" && typeof item?.reason === "string",
    )
    .slice(0, 6)
    .map(
      (item) => `${item.memberId.split("/").pop() ?? item.memberId} — ${ruleWords(item.reason)}`,
    );
  toastManager.add({
    type: "info",
    title:
      added.length === 1
        ? "Your rules added 1 item to this message"
        : `Your rules added ${added.length} items to this message`,
    description: `${lines.join("\n")}${added.length > lines.length ? `\n…and ${added.length - lines.length} more` : ""}\nAs References; remove any in Context and it stays out.`,
  });
}

/**
 * Arc SEL (BD-CAN-14): Send preparation reads the stored set through the
 * server's one door. The room may be unknown to the composer; the receipt
 * names it. An unseen change refreshes the displayed selection and refuses this
 * Send, so the person can review before the next attempt. No catalog
 * is read, so a catalog that never loaded cannot block Send.
 */
export async function prepareSendSelection(
  coordinate: {
    readonly worldKey: string;
    readonly roomId: string | null;
    readonly sessionId: string;
    readonly agentEnvironmentId: string;
    readonly agentThreadId: string;
  },
  messageId: string,
  expected?: { readonly selectionCut: number; readonly selectionDigest: string },
  liveCall?: { readonly requestKey: string; readonly delegationId: string },
  messageText?: string,
): Promise<SendSelectionOutcome> {
  try {
    const response = await fetchPrimary("/api/neucleos/context-selection/prepare-send", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        messageId,
        ...(liveCall ? { liveCall } : {}),
        worldKey: coordinate.worldKey,
        ...(coordinate.roomId === null ? {} : { roomId: coordinate.roomId }),
        sessionId: coordinate.sessionId,
        agentEnvironmentId: coordinate.agentEnvironmentId,
        agentThreadId: coordinate.agentThreadId,
        ...(expected === undefined
          ? {}
          : {
              expectedSelectionCut: expected.selectionCut,
              expectedSelectionDigest: expected.selectionDigest,
            }),
        ...(messageText ? { messageText: messageText.slice(0, SEND_MESSAGE_TEXT_MAX) } : {}),
      }),
    });
    const body = await jsonBody(response);
    const selection = body?.["selection"] as WorldContextSelection | undefined;
    if (selection === undefined) {
      if (body?.["kind"] === "context_changed" || body?.["kind"] === "selection_changed")
        return { _tag: "Refused", reason: "selection-mismatch" };
      if (body?.["kind"] === "source_unavailable")
        return { _tag: "Refused", reason: "source-unavailable" };
      // This Mac's sign-in ended (expired, revoked, or none for this World):
      // one press signs in again (W-a02f7279, W-c0ac93da).
      if (isReauthAvailableRefusal(body?.["kind"]))
        return { _tag: "Refused", reason: "sign-in-ended" };
      // Any other refusal carries the World's own reason, so the person reads
      // why the message was not sent (24 September 2026: a member's automatic
      // additions were refused and the composer said only "Try again").
      const worldReason = body?.["reason"];
      const detail = typeof worldReason === "string" ? worldReason.trim() : "";
      return {
        _tag: "Refused",
        reason: "selection-unavailable",
        ...(detail ? { detail: detail.slice(0, 800) } : {}),
      };
    }
    const receiptCoordinate = { ...coordinate, roomId: coordinate.roomId ?? selection.roomId };
    if (!(await validSelectionReceipt(selection, receiptCoordinate)))
      return { _tag: "Refused", reason: "selection-unavailable" };
    const resolution = body?.["resolution"] as AgentContextResolution | undefined;
    const reused = body?.["reused"] === true;
    // WSET4: the person's rules may add members at Send. The receipt names the
    // cut they started from; only the displayed cut may be moved that way.
    const autoTier = body?.["autoTier"] as
      | { readonly fromSelectionCut?: unknown; readonly fromSelectionDigest?: unknown }
      | undefined;
    const matchesDisplayed =
      expected === undefined ||
      (expected.selectionCut === selection.selectionCut &&
        expected.selectionDigest === selection.selectionDigest) ||
      (autoTier !== undefined &&
        autoTier.fromSelectionCut === expected.selectionCut &&
        autoTier.fromSelectionDigest === expected.selectionDigest);
    const accepted = response.ok && body?.["ok"] === true && (reused || matchesDisplayed);
    if (
      accepted &&
      (!resolution ||
        resolution.resolutionId !== `res-${messageId}` ||
        resolution.worldKey !== selection.worldKey ||
        resolution.roomId !== selection.roomId ||
        resolution.sessionId !== selection.sessionId ||
        resolution.agentEnvironmentId !== coordinate.agentEnvironmentId ||
        resolution.agentThreadId !== coordinate.agentThreadId ||
        resolution.selectionDigest !== selection.selectionDigest ||
        resolution.selectionCut !== selection.selectionCut ||
        resolution.workingContextBitmap !== selection.workingContextBitmap ||
        JSON.stringify(contextSelectionDigestValue(resolution.expressions)) !==
          JSON.stringify(contextSelectionDigestValue(selection.expressions)) ||
        JSON.stringify(resolution.memberIds) !== JSON.stringify(selection.memberIds) ||
        resolution.digest !== digestAgentContextResolution(resolution))
    )
      return { _tag: "Refused", reason: "selection-unavailable" };
    // A retry belongs to the original message. Its stored cut may be older than
    // the current rail and must neither be compared with it nor replace it.
    if (accepted && reused && resolution) return { _tag: "Ready", selection, resolution };
    const key = roomUniverseCoordinateKey(receiptCoordinate);
    const current = useRoomUniverseStore.getState().byCoordinate[key];
    const displayed = roomUniverseDisplayEntry(current);
    const known = roomUniverseKnownSelection(current);
    const moved =
      known !== undefined &&
      (known.selection.selectionCut !== selection.selectionCut ||
        known.selection.selectionDigest !== selection.selectionDigest);
    if (displayed?.state === "selection") {
      useRoomUniverseStore.setState((state) => {
        const entry = state.byCoordinate[key];
        const shown = roomUniverseDisplayEntry(entry);
        if (shown?.state !== "selection" || shown.selection.selectionCut > selection.selectionCut)
          return state;
        const next = { ...shown, selection };
        return {
          byCoordinate: {
            ...state.byCoordinate,
            [key]: entry?.state === "loading" ? { ...entry, previous: next } : next,
          },
        };
      });
    } else if (moved && displayed?.state === "paged") {
      // The summary owns all paged marks and counts. Changing only its head
      // would make a later Send admit choices that the rail still shows out.
      let reviewed: RoomUniversePagedEntry | null = null;
      try {
        reviewed = await readPagedSummary(receiptCoordinate);
      } catch {
        /* Keep the last shown cut. */
      }
      if (reviewed && reviewed.selection.selectionCut >= selection.selectionCut) {
        nextGeneration(roomRefreshGenerations, key);
        useRoomUniverseStore.setState((state) => ({
          byCoordinate: {
            ...state.byCoordinate,
            [key]: {
              ...reviewed,
              ...(current?.authorizationRevision === undefined
                ? {}
                : { authorizationRevision: current.authorizationRevision }),
            },
          },
        }));
      }
    } else if (
      moved &&
      (displayed?.state !== "complete" ||
        selection.memberIds.some((id) => !displayed.memberIds.includes(id)))
    ) {
      // An unavailable display or newly selected unknown member needs a real
      // metadata refresh before it can become the expected, reviewed cut.
      await useRoomUniverseStore.getState().refresh(receiptCoordinate, { force: true });
    } else if (displayed?.state === "complete") {
      useRoomUniverseStore.setState((state) => {
        const entry = state.byCoordinate[key];
        const shown = roomUniverseDisplayEntry(entry);
        if (shown?.state !== "complete" || shown.selection.selectionCut > selection.selectionCut)
          return state;
        const next = { ...shown, selection };
        return {
          byCoordinate: {
            ...state.byCoordinate,
            [key]: entry?.state === "loading" ? { ...entry, previous: next } : next,
          },
        };
      });
    }
    if (!response.ok || body?.["ok"] !== true || !matchesDisplayed)
      return { _tag: "Refused", reason: "selection-mismatch" };
    if (!resolution) return { _tag: "Refused", reason: "selection-unavailable" };
    announceAutoAdded(autoTier);
    return { _tag: "Ready", selection, resolution };
  } catch {
    return { _tag: "Refused", reason: "selection-unavailable" };
  }
}

export async function resolveSendCatalog(
  keyOrCoordinate: string | RoomUniverseCoordinate | null | undefined,
): Promise<MaterializedSelection> {
  const state = useRoomUniverseStore.getState();
  const key =
    typeof keyOrCoordinate === "string"
      ? keyOrCoordinate
      : keyOrCoordinate === null || keyOrCoordinate === undefined
        ? null
        : roomUniverseCoordinateKey(keyOrCoordinate);
  const entry = key === null ? undefined : state.byCoordinate[key];
  if (entry?.state === "paged") return state.materializeSelection(entry.coordinate);
  return selectUniverseMemberIds(state.byCoordinate, key);
}

export function selectUniverseMemberIds(
  entries: Readonly<Record<string, RoomUniverseEntry>>,
  keyOrCoordinate: string | RoomUniverseCoordinate | null | undefined,
): CatalogSelection {
  const key =
    typeof keyOrCoordinate === "string"
      ? keyOrCoordinate
      : keyOrCoordinate === null || keyOrCoordinate === undefined
        ? null
        : roomUniverseCoordinateKey(keyOrCoordinate);
  const entry = key === null ? undefined : entries[key];
  return entry?.state === "complete"
    ? {
        _tag: "Ready",
        memberIds: entry.memberIds,
        catalogDigest: entry.catalogDigest,
        catalogCustodyDigest: entry.catalogCustodyDigest,
        selection: entry.selection,
      }
    : { _tag: "Refused", reason: "catalog-unavailable" };
}
